import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, CheckCircle, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import PricingSection from '../components/PricingSection';
import ProcessSection from '../components/ProcessSection';
import ServicesSection from '../components/ServicesSection';

const Home = ({ onOpenPopup }) => {
  return (
    <>
      <Helmet>
        <title>Trademark Prime Unit - Professional Trademark Registration Services USA</title>
        <meta name="description" content="Expert trademark registration services to protect your brand identity. Fast, reliable, and affordable trademark protection for businesses across the USA. Get started today!" />
        <meta name="keywords" content="trademark registration, trademark services, brand protection, USPTO filing, intellectual property, trademark attorney" />
        <meta property="og:title" content="Trademark Prime Unit - Professional Trademark Registration Services" />
        <meta property="og:description" content="Protect your brand with expert trademark registration services. Fast processing, high success rate, trusted by 10,000+ businesses." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section with Background Image */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/assets/img/photo-1497366754035-f200968a6e72.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 via-primary-800/90 to-primary-700/95"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/30">
                  Trusted Trademark Registration Solutions
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Protect Your Brand,
                <span className="block mt-2 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  Build Your Future
                </span>
              </h1>
              <p className="text-xl text-secondary-100 leading-relaxed">
                Secure your business identity with professional trademark registration services. We provide fast, dependable, and attorney-guided trademark protection for businesses throughout the United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onOpenPopup}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 group border-2 border-white/20"
                >
                  Register Your Trademark
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => document.getElementById('process').scrollIntoView({ behavior: 'smooth' })}
                  size="lg"
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/40 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300"
                >
                  See How It Works
                </Button>
              </div>
              <div className="flex items-center space-x-8 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-6 w-6 text-yellow-400" />
                  <span className="text-white font-medium">Fast Turnaround</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-6 w-6 text-yellow-400" />
                  <span className="text-white font-medium">Safe & Reliable Service</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary-400/20 to-primary-600/20 rounded-3xl blur-3xl"></div>
              <img
                src="/assets/img/photo-1425421669292-0c3da3b8f529.jpg"
                alt="Professional trademark services"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover ring-4 ring-white/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="pt-20 pb-4 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/assets/img/pexels-photo-7876050.jpg"
                alt="Legal consultation"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Why Businesses Choose Trademark Prime Unit
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We provide end-to-end trademark registration services designed to protect your brand with confidence. Our knowledgeable team guides you through every stage of the registration process, helping ensure your application is prepared accurately and complies with USPTO requirements.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Experienced Legal Professionals</h3>
                    <p className="text-gray-600">Skilled trademark specialists and attorneys dedicated to protecting your intellectual property.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Complete Brand Protection</h3>
                    <p className="text-gray-600">Comprehensive trademark searches, filing support, and ongoing monitoring services.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Proven Success</h3>
                    <p className="text-gray-600">A strong history of helping businesses successfully register and protect their trademarks.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <div id="process">
        <ProcessSection />
      </div>

      {/* Services Section */}
      <ServicesSection onOpenPopup={onOpenPopup} />

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* CTA Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-secondary-100 mb-8 leading-relaxed">
            Don't leave your business identity unprotected. Start your trademark registration today and gain the confidence that comes with professional legal protection.
          </p>
          <Button
            onClick={onOpenPopup}
            size="lg"
            className="bg-white text-primary-800 hover:bg-secondary-50 px-12 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
          >
            Get Started Today
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
